/**
 * Scans a raw JSON string for duplicate top-level keys.
 * JSON.parse silently drops earlier values for duplicate keys, so this must run
 * against the raw string before parsing. Returns an error message if duplicates
 * are found, undefined otherwise. Returns undefined (no error) for invalid JSON
 * so normal parse-error handling takes over.
 *
 * Safe for flat Record<string, string> objects (e.g. allessehIdOverrides).
 * For deeply nested objects, any key repeated at any depth is flagged — pass only
 * flat objects to avoid false positives.
 */
export const detectDuplicateJsonKeys = (raw: string): string | undefined => {
  if (!raw.trim()) return undefined;
  try {
    JSON.parse(raw); // validate syntax first; let parse errors surface normally
  } catch {
    return undefined;
  }
  const keyPattern = /"([^"\\]*)"\s*:/g;
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  let match: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((match = keyPattern.exec(raw)) !== null) {
    const key = match[1];
    if (seen.has(key)) duplicates.add(key);
    else seen.add(key);
  }
  if (duplicates.size > 0) {
    return `Duplicate source key(s) found: ${[...duplicates].join(', ')}. The last value silently overwrites the first — remove duplicates to avoid losing a mapping.`;
  }
  return undefined;
};

// Returns a shallow copy of `obj` with null/undefined values removed.
// Useful for spreading optional fields onto GQL input objects without sending explicit nulls.
export const pickDefined = <T extends object>(obj: T): Partial<T> =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null)) as Partial<T>;

// Builds a normalizer function for a string enum. The map must have lowercase keys
// mapping to the canonical enum values. The returned function accepts any string and
// returns the canonical value if a case-insensitive match exists, otherwise returns
// the input unchanged.
export function makeEnumNormalizer<T extends string>(lowerMap: Map<string, T>): (v: string) => T {
  const values = new Set(lowerMap.values());
  return (v: string): T => (values.has(v as T) ? (v as T) : lowerMap.get(v.toLowerCase()) ?? (v as T));
}
